import React, { useState, useEffect } from "react";
import { Box, Alert, CircularProgress } from "@mui/material";
import httpClient from "../../../../../../Utils/index";
import TrackerHeader from "./components/TrackerHeader";
import MetroRouteProgress from "./components/MetroRouteProgress";
import RouteTimeline from "./components/RouteTimeline";

export default function DriversReportTracker({ driverId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch driver current delivery track data
  useEffect(() => {
    const fetchDriverTrackData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpClient.get(
          `/delivery/reports/drivers/current-delivery-track/${driverId}`
        );

        if (response.data && response.data.status === 200) {
          const apiData = response.data.data;
          const driverDetails = apiData.driver_current_track_details;
          const routeProgress = apiData.driver_delivery_current_route_progress;
          const routeDetails = apiData.driver_delivery_current_route_details;

          // Parse stop progress (e.g., "4/8")
          const completedStops = parseInt(
            driverDetails.current_stop_progress.split("/")[0]
          );

          // Parse order delivery progress (e.g., "4/10")
          const [deliveredOrders, totalOrders] =
            driverDetails.current_order_delivery_progress
              .split("/")
              .map(Number);

          // Transform route details to match component format
          const transformedStops = routeDetails.map((stop) => {
            // Map API stop_state to component status
            let status = "pending";
            if (stop.stop_state === "completed") {
              status = "completed";
            } else if (stop.stop_state === "next") {
              status = "next";
            }

            // Transform orders
            const orders = stop.order_list.map((order) => {
              // Map loaded_items from API
              const items =
                order.loaded_items?.map((item) => ({
                  code: item.item_code,
                  description: item.item_description,
                  quantity: item.item_loaded_qty,
                  remarks: item.delivery_order_line_remarks,
                  colorState: item.order_line_color_satate,
                })) || [];

              return {
                orderNo: order.order_number,
                customerName: order.customer_name,
                customerCode: order.customer_code,
                customerPhone: order.customer_phone
                  ? `+977-${order.customer_phone}`
                  : "N/A",
                status: order.order_state,
                priority: order.order_tag === "Urgent" ? "urgent" : "normal",
                items: items,
                orderDate: order.order_date,
                loadedTime: order.order_loaded,
                deliveredTime: order.order_delivered,
                deliveryStartTime: order.order_delivery_start,
                totalDeliveryTime : order.delivery_time_hours,
             
                notes: order.notes,
                other_files: order.other_files || [],
                signature_files: order.signature_files || [],
                ...order,
              };
            });

            return {
              stopId: `STOP-${String(stop.stop_number).padStart(2, "0")}`,
              stopNumber: stop.stop_number,
              location: stop.stop_name,
              address: stop.stop_name,
              status: status,
              orders: orders,
              returns_orders: stop.returns_orders || 0,
              partial_orders: stop.partial_orders || 0,
              total_delivered_order: stop.total_delivered_order || 0,
              timeline: [],
              estimatedArrival:
                status === "pending"
                  ? new Date(
                      Date.now() +
                        (stop.stop_number - completedStops) * 15 * 60000
                    ).toISOString()
                  : null,
            };
          });

          // Transform API data to match the component's expected format
          const transformedData = {
            driverId: driverId,
            driverName: driverDetails.driver_name || "N/A",
            driverPhone: driverDetails.driver_mobile || "N/A",
            vehicleId: driverDetails.driver_vehicle_no || "N/A",
            routeStartTime: new Date().toISOString(),
            totalStops: routeProgress.total_stop,
            completedStops: completedStops,
            currentStop: routeProgress.current_stop,
            currentStopName: routeProgress.current_stop_name,
            progressPercent: routeProgress.progress_percent,
            totalOrders: totalOrders,
            deliveredOrders: deliveredOrders,
            stops: transformedStops,
            routeList: routeProgress.route_list || [],
          };

          setData(transformedData);
        }
      } catch (err) {
        console.error("Error fetching driver track data:", err);
        setError(
          "Failed to load delivery tracking information. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (driverId) {
      fetchDriverTrackData();
    }
  }, [driverId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No delivery tracking data available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: 600, width: "100%" }}>
      {/* Header with driver info and progress */}
      <TrackerHeader data={data} />

      {/* Metro-Style Route Progress */}
      {data.stops.length > 0 && (
        <MetroRouteProgress
          currentStopNumber={data.currentStop}
          routeList={data.routeList}
        />
      )}

      {/* Route Timeline with stops and orders */}
      <RouteTimeline stops={data.stops} currentStop={data.currentStop} />
    </Box>
  );
}
